import { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import useRole from 'src/hooks/useRole';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { OfferType } from 'src/modules/generated/api';
import UserRole from 'src/types/UserRoles';

/**
 * Mirrors the visibility of the offer type filter, see `getFilter` in filter.tsx.
 */
export const useCanFilterOfferType = (): boolean => {
  const { hasRole } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);
  const packageViewFlagEnabled = useFeatureFlags('packageView');
  const hasPackageViewPermission = useUserPermissions(UserPermissions.PACKAGE_VIEW);

  return isAdmin || (packageViewFlagEnabled && hasPackageViewPermission);
};

/**
 * Authorized users always send an explicit offer type (ALL when unrestricted), everyone else none.
 */
const resolveOfferType = (offerType: OfferType | undefined, canFilterOfferType: boolean): OfferType =>
  canFilterOfferType ? offerType || OfferType.All : ('' as OfferType);

/**
 * Applies the rule before the form initializes. The persisted filter is used verbatim and may hold
 * a value the user can no longer see or correct, so normalizing here keeps the first request
 * correct - the effect below only runs after the table has already issued its query.
 */
export const normalizeOfferType = <TFilter extends { offerType?: OfferType }>(
  filter: TFilter,
  canFilterOfferType: boolean,
): TFilter => ({
  ...filter,
  offerType: resolveOfferType(filter.offerType, canFilterOfferType),
});

/**
 * Safety net for values written after mount, e.g. loading a search agent resets the whole form.
 */
export const useEnforceOfferType = ({
  form,
  name,
  canFilterOfferType,
  enabled,
}: {
  form: UseFormReturn<any>;
  name: 'offerType' | 'carsRequest.offerType';
  canFilterOfferType: boolean;
  /** Only enforce once the user - and with it the package view permission - is known. */
  enabled: boolean;
}): void => {
  const { control, setValue } = form;
  const offerType = useWatch({ control, name });

  useEffect(() => {
    if (!enabled) return;

    const normalized = resolveOfferType(offerType, canFilterOfferType);
    if (normalized !== offerType) {
      setValue(name, normalized);
    }
  }, [enabled, canFilterOfferType, name, offerType, setValue]);
};
